import React, { useState } from "react";
import { useMutation } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Avatar, Button, Dropdown, message, notification } from "antd";
import { ApartmentOutlined, MoonOutlined, SunOutlined } from "@ant-design/icons";
import { ChevronDown } from "react-feather";

import { completeOnboarding } from "../../services/auth";
import * as ProjectApi from "../../services/projects";
import * as OrganizationApi from "../../services/organization";
import { bulkInviteUsers } from "../../services/users";
import { actions as AuthActions } from "../../store/authRedux/actions";
import { actions as CommonActions } from "../../store/commonRedux/actions";
import { useTheme } from "../../hooks";
import { switchOrganization } from "../../utils/orgSwitch";
import { needsOnboarding } from "../../utils/onboardingGate";
import { toFa } from "../../utils/helpers";
import ModulesStep from "./ModulesStep";
import ProjectStep from "./ProjectStep";
import InviteStep from "./InviteStep";
import styles from "./styles.module.css";

/* The setup wizard. Routes.js renders this instead of the panel while the
 * organization has no `onboarded_at`, so it is a gate, not a suggestion: there
 * is no sidebar to wander into and nothing to dismiss. Each individual step is
 * skippable — a workspace with no project and no colleagues is a legitimate
 * thing to want — but the flow itself has to be walked to the end.
 *
 * Step one (name, logo, industry) happens on the register screen, before the
 * organization exists and therefore before any organization-scoped endpoint can
 * be called. That is why the wizard picks up at step two. */

const FIRST_STEP = 2;
const LAST_STEP = 4;

const Onboarding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDark, toggle } = useTheme();

  const organization = useSelector(({ auth }) => auth.organization);
  const allOrganizations = useSelector(({ auth }) => auth.allOrganizations);

  /* Without this the wizard is a trap for anyone who already has a workspace:
   * creating a second organization switches to it, and until its setup is
   * finished there is no sidebar and no way back to the one they were using.
   * Only workspaces that are past setup are offered — switching into another
   * half-finished one would just move the trap. */
  const otherOrganizations = (allOrganizations || []).filter(
    (org) => org?.uuid !== organization?.uuid && !needsOnboarding(org),
  );

  const [step, setStep] = useState(FIRST_STEP);
  const [project, setProject] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);

  /* Saved on the organization, so the answer holds on the next device and for
   * every colleague who joins — the sidebar reads it back off the organization
   * the API returns. The step moves on either way: this is a preference about
   * what the menu shows, and a failed save is not worth stranding someone in
   * the middle of setup over. */
  const modulesMutate = useMutation(OrganizationApi.updateOrganizationModules, {
    onSuccess: (res) => {
      if (res?.data?.organization) {
        dispatch(AuthActions.setOrganization(res.data.organization));
      }
      setStep(3);
    },
    onError: () => {
      message.error({ content: "ذخیره‌ی ماژول‌ها انجام نشد" });
      setStep(3);
    },
  });

  const saveModules = (modules) => modulesMutate.mutate({ body: { modules } });

  const finish = (target) => {
    /* The board picks the aha up from here: it shows a one-off prompt to hand a
     * task to one of the people just invited. It is keyed to this project so it
     * can only ever appear for the board this wizard built — a hint keyed on
     * "not yet dismissed" would greet every existing user on every board. */
    if (project?.uuid && inviteResult?.invited?.length) {
      dispatch(CommonActions.setVariables({ boardAssignHint: project.uuid }));
    }

    /* Navigate first: once `onboarded_at` lands in the store this component is
     * unmounted and replaced by the panel, which then renders at whatever route
     * we are on. */
    navigate(target || "/", { replace: true });

    /* The owner has just been welcomed at length; the one-screen welcome meant
     * for invited colleagues must not greet them again on the other side. */
    if (organization?.uuid) {
      dispatch(CommonActions.seeOrgWelcome(organization.uuid));
    }

    completeOnboardingMutate.mutate();
  };

  const completeOnboardingMutate = useMutation(completeOnboarding, {
    onSuccess: (res) => {
      dispatch(AuthActions.setOrganization(res.organization));
    },
    onError: () => {
      /* The wizard is done from the user's point of view either way — they are
       * already looking at the panel. Leaving `onboarded_at` unset only means
       * the wizard reappears on the next sign-in, which is recoverable; showing
       * an error over a board they just built is not worth it. */
    },
  });

  const createProjectMutate = useMutation(ProjectApi.createProject, {
    onSuccess: (res) => {
      setProject(res?.data?.project);
      setStep(4);
    },
    onError: () => {},
  });

  const inviteMutate = useMutation(bulkInviteUsers, {
    onSuccess: (res) => {
      const invited = res?.data?.invited || [];
      const skipped = res?.data?.skipped || [];

      if (!invited.length) {
        notification.warning({ className: "ds-notif",
          message: "هیچ‌کدوم از شماره‌ها اضافه نشدن",
          description: "یا قبلاً عضو این سازمان بودن یا شماره‌شون درست نبود.",
        });
        return;
      }

      setInviteResult({ invited, skipped });

      if (res?.data?.limit_reached) {
        notification.info({ className: "ds-notif",
          message: "سقف کاربران پلن فعلی پر شد",
          description: "برای دعوت بقیه‌ی تیم، پلنت رو ارتقا بده.",
        });
      }
    },
    onError: () => {},
  });

  const boardPath = project?.uuid ? `/projects/edit/${project.uuid}` : "/";

  return (
    <main className={`${styles.shell} ds-auth-canvas`}>
      <Button
        className={styles.themeToggle}
        type="text"
        shape="circle"
        aria-label={isDark ? "فعال‌کردن تم روشن" : "فعال‌کردن تم تیره"}
        icon={isDark ? <SunOutlined /> : <MoonOutlined />}
        onClick={toggle}
      />

      {/* Just the current workspace's name, as the way out: creating a second
        * organization switches to it, and until its setup is finished there is
        * no sidebar to switch back from. Workspaces still in setup are left out
        * of the list — moving into another unfinished one only moves the
        * problem. */}
      {otherOrganizations.length ? (
        <Dropdown
          trigger={["click"]}
          placement="bottomLeft"
          menu={{
            items: otherOrganizations.map((org) => ({
              key: org.uuid,
              label: (
                <div className="d-flex align-items-center gap-2">
                  <Avatar
                    size="small"
                    src={org?.avatar?.original_url}
                    icon={<ApartmentOutlined />}
                  />
                  <span style={{ fontWeight: 600 }}>{org?.title}</span>
                </div>
              ),
              onClick: () => switchOrganization(org),
            })),
          }}
        >
          <div className={`org-chip ${styles.orgChip}`}>
            {organization?.avatar ? (
              <Avatar size={22} src={organization.avatar?.original_url} />
            ) : null}
            <span className={styles.orgName}>{organization?.title}</span>
            <ChevronDown style={{ width: 14, color: "var(--ds-text-muted)" }} />
          </div>
        </Dropdown>
      ) : null}

      <div className={styles.brandMark}>
        <span className={styles.brandLogo}>
          <img src="/logo.svg" alt="" />
        </span>
        <div>
          <strong>{organization?.title || "تسکامون"}</strong>
          <small>چند قدم مونده تا فضای کاریت آماده بشه</small>
        </div>
      </div>

      <section className={styles.card}>
        <div className={styles.progress}>
          {[1, 2, 3, 4].map((index) => (
            <span
              key={index}
              className={`${styles.progressBar} ${index <= step ? styles.progressBarDone : ""}`}
            />
          ))}
          <span className={styles.progressLabel}>
            قدم {toFa(step)} از {toFa(LAST_STEP)}
          </span>
        </div>

        {step === 2 && (
          <ModulesStep
            loading={modulesMutate.isLoading}
            onSubmit={saveModules}
            onSkip={saveModules}
          />
        )}

        {step === 3 && (
          <ProjectStep
            loading={createProjectMutate.isLoading}
            onSubmit={({ title, template }) =>
              createProjectMutate.mutate({
                body: {
                  title,
                  ...(template?.id ? { project_template_id: template.id } : {}),
                },
              })
            }
            onSkip={() => setStep(4)}
          />
        )}

        {step === 4 && (
          <InviteStep
            loading={inviteMutate.isLoading}
            result={inviteResult}
            onSubmit={(mobiles) => inviteMutate.mutate({ body: { mobiles } })}
            onSkip={() => finish(boardPath)}
            onFinish={() => finish(boardPath)}
          />
        )}
      </section>
    </main>
  );
};

export default Onboarding;
